import React from 'react';

/**
 * Modal de detalle de envío: cliente, dirección, teléfono, monto,
 * productos (expandiendo paquetes si tienen expanded_items) y notas.
 * Se usa en el mapa (clic en pin/tarjeta) y en la vista de Mi deuda.
 */
export default function ShipmentDetailModal({ shipment, onClose, actions }) {
  if (!shipment) return null;
  const s = shipment;
  const items = Array.isArray(s.expanded_items) && s.expanded_items.length
    ? s.expanded_items
    : (Array.isArray(s.items) ? s.items : []);
  const total = (Number(s.total) > 0 ? Number(s.total) : Number(s.items_total || 0) + Number(s.shipping_cost || 0));
  const totalUnits = items.reduce((acc, it) => acc + Number(it.quantity || 0), 0);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="shipment-detail-modal">
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-slate-200 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 break-words">{s.client_name || 'Cliente'}</p>
            {s.folio && <p className="text-[11px] text-slate-400">Folio: {s.folio}</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none" data-testid="shipment-detail-close">×</button>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-3 text-sm">
          {(s.shipping_address || s.shipping_colonia) && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-0.5">📍 Dirección</p>
              <p className="text-slate-700 break-words">{s.shipping_address || s.shipping_colonia}</p>
              {s.shipping_address && s.shipping_colonia && s.shipping_address !== s.shipping_colonia && (
                <p className="text-xs text-slate-500 mt-0.5">Colonia: {s.shipping_colonia}</p>
              )}
            </div>
          )}

          {s.client_phone && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-0.5">📞 Teléfono</p>
              <a href={`tel:${s.client_phone}`} className="text-blue-600 hover:underline">{s.client_phone}</a>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 text-xs">
            {total > 0 && (
              <div className="bg-emerald-50 rounded-lg p-2">
                <p className="text-[10px] uppercase text-emerald-600 font-semibold">Total a cobrar</p>
                <p className="text-emerald-800 font-bold text-base">${total.toFixed(2)}</p>
              </div>
            )}
            {typeof s.shipping_cost === 'number' && (
              <div className="bg-blue-50 rounded-lg p-2">
                <p className="text-[10px] uppercase text-blue-600 font-semibold">Envío</p>
                <p className="text-blue-800 font-bold text-base">${Number(s.shipping_cost).toFixed(2)}</p>
                {s.deliverer_keeps_shipping && <p className="text-[10px] text-emerald-700 mt-0.5">Se queda contigo</p>}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">🧾 Productos</p>
              {totalUnits > 0 && <span className="text-[10px] text-slate-500">({totalUnits} pza)</span>}
            </div>
            {items.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Sin productos registrados</p>
            ) : (
              <ul className="space-y-1">
                {items.map((it, idx) => (
                  <li key={idx} className="flex items-start gap-2 bg-slate-50 rounded-md px-2 py-1.5">
                    <span className="flex-shrink-0 min-w-[28px] h-6 flex items-center justify-center bg-red-600 text-white rounded text-[11px] font-bold px-1.5">
                      {it.quantity}×
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-800 break-words">{it.product_name || it.name || 'Producto'}{it.variant_name ? ` — ${it.variant_name}` : ''}</p>
                      {it.from_package && (
                        <p className="text-[10px] text-purple-600 mt-0.5">📦 Del paquete: {it.from_package}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {s.notes && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-0.5">📝 Notas</p>
              <p className="text-slate-700 text-xs whitespace-pre-wrap break-words bg-amber-50 border border-amber-200 rounded p-2">{s.notes}</p>
            </div>
          )}
        </div>

        {actions && (
          <div className="p-3 border-t border-slate-200 flex gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
